/**
 * THE single source of truth for every campaign-related status string in the UI.
 *
 * ONE flat constant — `CAMPAIGN_STATUSES` — enumerates every status token the client knows, grouped
 * by the LAYER it belongs to and PREFIXED so the layers never collide even where the string value
 * repeats (`QUEUE_SCHEDULED` and `ADMIN_SCHEDULED` are both "Scheduled"; `LINK_EXPIRED` is
 * "Expired"). Every status type and list below is DERIVED from it — nothing else in the UI declares
 * a status literally. Mirrors the API's `CAMPAIGN_STATUS`
 * (apps/gfgf-api/src/constants/campaignStatuses.ts); the API additionally carries the legacy
 * STORED_* varchar, which the client never sees.
 *
 * The campaign LIFECYCLE values are lowercase (the API's `deriveCampaignStatus` output, rendered
 * verbatim). The link / send-queue / bounce / admin values are Title-Case — the strings the API
 * returns on the wire.
 */
export const CAMPAIGN_STATUSES = {
  // ── Derived campaign lifecycle (lowercase) — the 8-status model (CAMPAIGN_RULES v1.3) ──────────
  DRAFT: "draft",
  SCHEDULED: "scheduled",
  ACTIVE: "active",
  // Deliberate-act overlays, derived from the overlay columns server-side.
  PAUSED: "paused",
  CLOSED: "closed",
  CANCELED: "canceled",
  ARCHIVED: "archived",
  /** Soft-deleted — excluded from every client read; never rendered on a client card. */
  DELETED: "deleted",
  /** Universal Link only: the client turned the shared /u/ link off. SHADOWS active (reversible via
   *  Enable) — the API derives it from the `disabled_at` overlay. */
  DISABLED: "disabled",

  // ── Per-recipient link status (Title-Case) — a campaign is `active` while its links are each ────
  // Pending / Sent / Redeemed / Declined / Expired / Bounced.
  LINK_PENDING: "Pending",
  LINK_SENT: "Sent",
  LINK_REDEEMED: "Redeemed",
  LINK_DECLINED: "Declined",
  LINK_EXPIRED: "Expired",
  LINK_BOUNCED: "Bounced",

  // ── Send-queue status (Title-Case) — the state of one upcoming SEND, not its campaign ──────────
  // There is no 'Live': a running campaign is 'active' (the lifecycle above), and a sent send has
  // left the queue — a pending send is only Sending / Scheduled / Paused.
  QUEUE_SENDING: "Sending",
  QUEUE_SCHEDULED: "Scheduled",
  QUEUE_PAUSED: "Paused",

  // ── Bounce status (Reports → Bounces, Title-Case) ──────────────────────────────────────────────
  BOUNCE_UNRESOLVED: "Unresolved",
  BOUNCE_RESENT: "Resent",
  BOUNCE_RESOLVED: "Resolved",

  // ── Admin My-Campaigns table presentation (Title-Case) ─────────────────────────────────────────
  // A running campaign is 'Active', never 'Live' — the admin table matches the client lifecycle word.
  ADMIN_DRAFT: "Draft",
  ADMIN_SCHEDULED: "Scheduled",
  ADMIN_ACTIVE: "Active",
  ADMIN_PAUSED: "Paused",
  ADMIN_CLOSED: "Closed",
  ADMIN_CANCELED: "Cancelled",
  ADMIN_ARCHIVED: "Archived",
  ADMIN_DISABLED: "Disabled",
} as const;

// ── Derived campaign lifecycle ─────────────────────────────────────────────────────────────────

/** The 8-status campaign lifecycle (the API's `deriveCampaignStatus` vocabulary). */
export const CAMPAIGN_LIFECYCLE_STATUSES = [
  CAMPAIGN_STATUSES.DRAFT,
  CAMPAIGN_STATUSES.SCHEDULED,
  CAMPAIGN_STATUSES.ACTIVE,
  CAMPAIGN_STATUSES.PAUSED,
  CAMPAIGN_STATUSES.CLOSED,
  CAMPAIGN_STATUSES.CANCELED,
  CAMPAIGN_STATUSES.ARCHIVED,
  CAMPAIGN_STATUSES.DELETED,
  CAMPAIGN_STATUSES.DISABLED,
] as const;

/** The status of a CAMPAIGN itself. Mirrors the API's `deriveCampaignStatus` vocabulary. */
export type CampaignStatus = (typeof CAMPAIGN_LIFECYCLE_STATUSES)[number];

// ── Per-recipient link status ──────────────────────────────────────────────────────────────────

export type CampaignLinkStatus =
  | typeof CAMPAIGN_STATUSES.LINK_PENDING
  | typeof CAMPAIGN_STATUSES.LINK_SENT
  | typeof CAMPAIGN_STATUSES.LINK_REDEEMED
  | typeof CAMPAIGN_STATUSES.LINK_DECLINED
  | typeof CAMPAIGN_STATUSES.LINK_EXPIRED
  | typeof CAMPAIGN_STATUSES.LINK_BOUNCED;

/** The link vocabulary as a list — for filter options and `includes` checks. */
export const CAMPAIGN_LINK_STATUS_VALUES = [
  CAMPAIGN_STATUSES.LINK_PENDING,
  CAMPAIGN_STATUSES.LINK_SENT,
  CAMPAIGN_STATUSES.LINK_REDEEMED,
  CAMPAIGN_STATUSES.LINK_DECLINED,
  CAMPAIGN_STATUSES.LINK_EXPIRED,
  CAMPAIGN_STATUSES.LINK_BOUNCED,
] as const satisfies readonly CampaignLinkStatus[];

// ── Send-queue status ──────────────────────────────────────────────────────────────────────────

export type SendQueueStatus =
  | typeof CAMPAIGN_STATUSES.QUEUE_SENDING
  | typeof CAMPAIGN_STATUSES.QUEUE_SCHEDULED
  | typeof CAMPAIGN_STATUSES.QUEUE_PAUSED;

// ── Bounce status (Reports → Bounces) ──────────────────────────────────────────────────────────

export type BounceStatus =
  | typeof CAMPAIGN_STATUSES.BOUNCE_UNRESOLVED
  | typeof CAMPAIGN_STATUSES.BOUNCE_RESENT
  | typeof CAMPAIGN_STATUSES.BOUNCE_RESOLVED;

/** The bounce vocabulary keyed for `.UNRESOLVED`-style access, derived from the one constant. */
export const BOUNCE_STATUSES = {
  UNRESOLVED: CAMPAIGN_STATUSES.BOUNCE_UNRESOLVED,
  RESENT: CAMPAIGN_STATUSES.BOUNCE_RESENT,
  RESOLVED: CAMPAIGN_STATUSES.BOUNCE_RESOLVED,
} as const;

// ── Admin My-Campaigns table presentation ──────────────────────────────────────────────────────

export type AdminCampaignStatus =
  | typeof CAMPAIGN_STATUSES.ADMIN_DRAFT
  | typeof CAMPAIGN_STATUSES.ADMIN_SCHEDULED
  | typeof CAMPAIGN_STATUSES.ADMIN_ACTIVE
  | typeof CAMPAIGN_STATUSES.ADMIN_PAUSED
  | typeof CAMPAIGN_STATUSES.ADMIN_CLOSED
  | typeof CAMPAIGN_STATUSES.ADMIN_CANCELED
  | typeof CAMPAIGN_STATUSES.ADMIN_ARCHIVED
  | typeof CAMPAIGN_STATUSES.ADMIN_DISABLED;

export const ADMIN_CAMPAIGN_STATUSES: readonly AdminCampaignStatus[] = [
  CAMPAIGN_STATUSES.ADMIN_DRAFT,
  CAMPAIGN_STATUSES.ADMIN_SCHEDULED,
  CAMPAIGN_STATUSES.ADMIN_ACTIVE,
  CAMPAIGN_STATUSES.ADMIN_PAUSED,
  CAMPAIGN_STATUSES.ADMIN_CLOSED,
  CAMPAIGN_STATUSES.ADMIN_CANCELED,
  CAMPAIGN_STATUSES.ADMIN_ARCHIVED,
  CAMPAIGN_STATUSES.ADMIN_DISABLED,
];

/** How one status is drawn: pill background, label color, and the dot that precedes it. */
export interface StatusVisual {
  label: string;
  bgColor: string;
  textColor: string;
  dotColor: string;
  /** The dot animates instead of sitting still — a send that is in flight right now. */
  pulse?: boolean;
  /** Strike the label through — a campaign that was deliberately stopped (Canceled). */
  strike?: boolean;
  /** Color of the strike-through rule when `strike` is set. */
  strikeColor?: string;
}

/**
 * THE status palette — every status a campaign can show, campaign-level and link-level alike, in
 * one map. `CampaignStatusChip` is the only component that reads it, so a status added here is
 * rendered correctly everywhere a chip appears without touching a component.
 *
 * Keys are the canonical lowercase form of the status; `statusVisual()` lowercases what it is
 * given. `dotColor` mirrors `textColor` for every entry, which is the convention this palette has
 * always followed.
 */
export const STATUS_VISUALS: Record<string, StatusVisual> = {
  // ── Campaign statuses ──────────────────────────────────────────────────────────────────────
  // Palette per the client-experience prototype: Draft/Archived = grey (smoke), Scheduled = ochre
  // (waiting), Active = blue (running), Closed = green (finished). Blue = "running", green = "done".
  draft: {
    label: "Draft",
    bgColor: "var(--color-smoke)",
    textColor: "var(--color-graphite)",
    dotColor: "var(--color-graphite)",
  },
  scheduled: {
    label: "Scheduled",
    bgColor: "var(--color-good-ochre-4)",
    textColor: "var(--color-good-ochre)",
    dotColor: "var(--color-good-ochre)",
  },
  active: {
    label: "Active",
    bgColor: "var(--color-good-night-4)",
    textColor: "var(--color-good-night)",
    dotColor: "var(--color-good-night)",
  },
  closed: {
    label: "Closed",
    bgColor: "var(--color-good-sage-4)",
    textColor: "var(--color-good-sage)",
    dotColor: "var(--color-good-sage)",
  },
  // Deliberately stopped before it sent. Neutral wax pill, label struck through — "this was called
  // off", distinct from a Draft (same palette, no strike) and from a Closed campaign that ran.
  canceled: {
    label: "Canceled",
    bgColor: "var(--color-wax)",
    textColor: "var(--color-graphite)",
    dotColor: "var(--color-graphite)",
    strike: true,
    strikeColor: "var(--color-rodeo-dust)",
  },
  // Finished and tidied out of the default list. Its own quiet grey, one shade darker than Draft's
  // wax, rather than borrowing Closed's — "put away", not "ended".
  archived: {
    label: "Archived",
    bgColor: "var(--color-smoke)",
    textColor: "var(--color-graphite)",
    dotColor: "var(--color-graphite)",
  },
  // ── Campaign-link statuses ─────────────────────────────────────────────────────────────────
  pending: {
    label: "Pending",
    bgColor: "var(--color-light-dust)",
    textColor: "var(--color-dark-dust)",
    dotColor: "var(--color-dark-dust)",
  },
  sent: {
    label: "Sent",
    bgColor: "var(--color-smoke)",
    textColor: "var(--color-graphite)",
    dotColor: "var(--color-graphite)",
  },
  // Display-only: a Sent link whose email was opened. Never a stored or filterable link status.
  opened: {
    label: "Opened",
    bgColor: "var(--color-good-night-4)",
    textColor: "var(--color-good-night)",
    dotColor: "var(--color-good-night)",
  },
  redeemed: {
    label: "Redeemed",
    bgColor: "var(--color-good-sage-4)",
    textColor: "var(--color-good-sage)",
    dotColor: "var(--color-good-sage)",
  },
  // The recipient said no, from the link in their reminder email. Its own pill rather than a
  // shade of Expired: nobody ran out of time, someone made a decision.
  declined: {
    label: "Declined",
    bgColor: "var(--color-wax)",
    textColor: "var(--color-dark-dust)",
    dotColor: "var(--color-dark-dust)",
  },
  expired: {
    label: "Expired",
    bgColor: "var(--color-wax)",
    textColor: "var(--color-graphite)",
    dotColor: "var(--color-graphite)",
  },
  bounced: {
    label: "Bounced",
    bgColor: "var(--color-good-wine-bg)",
    textColor: "var(--color-good-wine)",
    dotColor: "var(--color-good-wine)",
  },
  // ── Bounce statuses (Reports → Bounces) ─────────────────────────────────────────────────
  unresolved: {
    label: "Unresolved",
    bgColor: "var(--color-good-wine-bg)",
    textColor: "var(--color-good-wine)",
    dotColor: "var(--color-good-wine)",
  },
  resent: {
    label: "Resent",
    bgColor: "var(--color-good-night-4)",
    textColor: "var(--color-good-night)",
    dotColor: "var(--color-good-night)",
  },
  resolved: {
    label: "Resolved",
    bgColor: "var(--color-good-sage-4)",
    textColor: "var(--color-good-sage)",
    dotColor: "var(--color-good-sage)",
  },
  // ── Send-queue statuses ────────────────────────────────────────────────────────────────────
  // The state of one upcoming SEND, which is not the state of its campaign: an Active campaign
  // owns a Scheduled reminder. `scheduled` above is shared with them; these two are the rest.
  // (There is no `live` — a running campaign is `active`; a sent send has left the queue.)
  sending: {
    label: "Sending",
    bgColor: "var(--color-good-night-bg)",
    textColor: "var(--color-good-night)",
    dotColor: "var(--color-good-night)",
    pulse: true,
  },
  paused: {
    label: "Paused",
    bgColor: "var(--color-good-wine-4)",
    textColor: "var(--color-good-wine)",
    dotColor: "var(--color-good-wine)",
  },
  // Universal Link turned off by its client — a reversible "off" (Enable restores it to active). A quiet
  // wax pill (no strike — it's not terminal like canceled), distinct from Active's blue and Paused's wine.
  disabled: {
    label: "Disabled",
    bgColor: "var(--color-wax)",
    textColor: "var(--color-graphite)",
    dotColor: "var(--color-graphite)",
  },
};

/** Legacy / alternate spellings the API still emits, mapped onto a key STATUS_VISUALS has. */
const STATUS_ALIAS: Record<string, string> = {
  // `archived` now has its own pill (above); only the legacy `completed` spelling still folds onto closed.
  completed: "closed",
};

/**
 * The quietest pill in the palette, used for a status this build has no entry for.
 *
 * An unknown value is rendered as ITSELF in neutral colors rather than mislabelled as some other
 * status: the vocabulary is still growing (an "Opened" or "Failed" link state is on the table), and
 * a stale bundle meeting a newer API must not tell the user a Bounced link is a Draft.
 */
const UNKNOWN_VISUAL = {
  bgColor: "var(--color-wax)",
  textColor: "var(--color-graphite)",
  dotColor: "var(--color-graphite)",
} as const;

/** How to draw `status`, whatever vocabulary it belongs to. Never throws on an unknown value. */
export function statusVisual(status: string): StatusVisual {
  const key = status.toLowerCase();
  return STATUS_VISUALS[STATUS_ALIAS[key] ?? key] ?? { ...UNKNOWN_VISUAL, label: status };
}

/** Label for the causes a campaign's unused gift credits are split between (rollover). */
export const CAUSE_BENEFICIARIES_LABEL = "Cause Beneficiaries";

/** Copy of the "Your Cause Beneficiary" modal opened from the My Campaigns card (GFG-275). */
export const CAUSE_BENEFICIARY_MODAL = {
  TITLE_SINGULAR: "Your Cause Beneficiary",
  TITLE_PLURAL: "Your Cause Beneficiaries",
  BODY_BEFORE_ORGS:
    "You have chosen to allocate any unredeemed credits from this gift campaign to support ",
  BODY_AFTER_ORGS: ". Thank you for being awesome!",
  CLOSE_LABEL: "Close",
} as const;

/**
 * Copy shown when a campaign WRITE is refused, paired with `writeDenialMessage` at the call site:
 * `denied` for an access refusal (401/403 — the UI offered an action the API won't allow, which
 * row-scope makes possible), `fallback` for any other failure. The raw "Forbidden: out of scope"
 * body is never shown. Centralised here so the My Campaigns card actions and the builder's draft
 * persistence can't drift onto different wording.
 */
export const CAMPAIGN_WRITE_ERROR = {
  clone: {
    denied: "You don't have access to clone this campaign.",
    fallback: "We couldn't clone your campaign. Please try again.",
  },
  delete: {
    denied: "You don't have access to delete this campaign.",
    fallback: "We couldn't delete your campaign. Please try again.",
  },
  rename: {
    denied: "You don't have access to rename this campaign.",
    fallback: "We couldn't rename your campaign. Please try again.",
  },
} as const;

/**
 * The same, for the Campaign Builder's draft persistence — keyed by the verb the hook logs
 * (Save / Create / Load / Delete). Kept apart from the card actions above because the hook looks
 * these up dynamically by verb; a verb with no entry falls to `CAMPAIGN_WRITE_ERROR_GENERIC`.
 */
export const CAMPAIGN_DRAFT_WRITE_ERROR: Record<string, { denied: string; fallback: string }> = {
  Save: {
    denied: "You don't have access to edit this campaign.",
    fallback: "Your changes did not save. Please try again.",
  },
  Create: {
    denied: "You don't have access to create a campaign here.",
    fallback: "We couldn't create your campaign. Please try again.",
  },
  Load: {
    denied: "You don't have access to open this campaign.",
    fallback: "We couldn't open your campaign. Please try again.",
  },
  Delete: {
    denied: "You don't have access to delete this campaign.",
    fallback: "We couldn't archive your campaign. Please try again.",
  },
};

/** Last-resort copy for a campaign write whose verb has no specific entry above. */
export const CAMPAIGN_WRITE_ERROR_GENERIC = {
  denied: "You don't have access to do that.",
  fallback: "Something went wrong. Please try again.",
} as const;
